import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { UploadFile, CloudUpload } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import api from '../services/api';
import { formatMoney } from '../components/commercial/contact';

type Row = any[];
type ProductDraft = { code: string; name: string; stock: number | null; unitCost: string; price: string };

const norm = (v: any) => String(v ?? '').trim().toUpperCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
const num = (v: any): number | null => {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return v;
  const n = Number(String(v).replace(/[$\s.]/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};
const toDate = (v: any): Date | null => {
  if (v instanceof Date) return v;
  if (typeof v === 'number') { const d = XLSX.SSF.parse_date_code(v); return d ? new Date(d.y, d.m - 1, d.d) : null; }
  return null;
};
const isoDay = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T00:00:00Z`;

function sheet(wb: XLSX.WorkBook, name: string): Row[] {
  const key = wb.SheetNames.find(n => norm(n) === norm(name));
  return key ? (XLSX.utils.sheet_to_json(wb.Sheets[key], { header: 1, raw: true, defval: null }) as Row[]) : [];
}

/**
 * Lee la planilla de gestión del cliente (hojas INVENTARIO, CLIENTES y VENTA MAYORISTA) y arma
 * productos con stock, insumos de packaging, clientes con precio pactado y el historial de ventas.
 */
function parseWorkbook(wb: XLSX.WorkBook) {
  // ---- INVENTARIO: bloque de packaging (TIPO, UNIDADES, $ UNITARIO) y de productos (TIPO, CODIGO, UNIDADES) ----
  const inv = sheet(wb, 'INVENTARIO');
  const products: ProductDraft[] = [];
  const materials: { name: string; stock: number | null; cost: number | null }[] = [];
  if (inv.length) {
    const h = inv[0].map(norm);
    const codeCol = h.indexOf('CODIGO');
    // El nombre del producto está en la columna TIPO más cercana a la izquierda de CODIGO.
    const prodName = h.slice(0, Math.max(0, codeCol)).lastIndexOf('TIPO');
    const prodUnits = codeCol + 1;
    for (const r of inv.slice(1)) {
      if (codeCol > 0 && typeof r[codeCol] === 'string' && r[codeCol].trim() && typeof r[prodName] === 'string') {
        products.push({ code: r[codeCol].trim().toUpperCase(), name: r[prodName].trim(), stock: num(r[prodUnits]), unitCost: '', price: '' });
      }
      if (typeof r[0] === 'string' && r[0].trim() && codeCol !== 0 && norm(r[0]) !== 'TIPO') {
        materials.push({ name: r[0].trim(), stock: num(r[1]), cost: num(r[2]) });
      }
    }
  }

  // ---- CLIENTES ----
  const cl = sheet(wb, 'CLIENTES');
  const customers: any[] = [];
  if (cl.length) {
    const h = cl[0].map(norm);
    const col = (name: string) => h.indexOf(name);
    const codes = new Set(products.map(p => p.code));
    const priceCols = h.map((x, i) => ({ x, i })).filter(c => codes.has(c.x));
    for (const r of cl.slice(1)) {
      const name = r[col('NOMBRE')];
      if (typeof name !== 'string' || !name.trim()) continue;
      customers.push({
        name: name.trim(),
        customerType: r[col('TIPO COMERCIO')] ?? null,
        branchCount: num(r[col('PDV')]) || 0,
        address: r[col('DIRECCION')] ? String(r[col('DIRECCION')]) : null,
        city: r[col('LOCALIDAD')] ? String(r[col('LOCALIDAD')]) : null,
        province: r[col('PROVINCIA')] ? String(r[col('PROVINCIA')]) : null,
        contact: r[col('CONTACTO')] ? String(r[col('CONTACTO')]) : null,
        phone: r[col('TELEFONO')] ? String(r[col('TELEFONO')]) : null,
        cuit: r[col('CUIT')] ? String(Math.round(Number(r[col('CUIT')])) || r[col('CUIT')]) : null,
        category: col('CATEGORIA') >= 0 && r[col('CATEGORIA')] ? String(r[col('CATEGORIA')]) : null,
        route: col('RECORRIDA') >= 0 && typeof r[col('RECORRIDA')] === 'string' ? r[col('RECORRIDA')] : null,
        // Columna de precio por código (BAL-CLA…) y la siguiente es su PVP sugerido.
        prices: priceCols.map(c => ({ code: c.x, price: num(r[c.i]) || 0, suggestedRetailPrice: num(r[c.i + 1]) })).filter(p => p.price > 0),
      });
    }
  }

  // ---- VENTA MAYORISTA ----
  const vm = sheet(wb, 'VENTA MAYORISTA');
  const sales: any[] = [];
  if (vm.length) {
    const h = vm[0].map(norm);
    const col = (name: string, fallback: number) => (h.indexOf(name) >= 0 ? h.indexOf(name) : fallback);
    const cDate = h.indexOf('FECHA') >= 0 ? h.indexOf('FECHA') : 0;
    const cCust = col('CLIENTE', 1), cProd = col('PRODUCTO', 2), cQty = col('CANT', 3), cPrice = col('$ UNITARIO', 4);
    const cPaid = col('PAGADO', 7), cMethod = col('FORMA', 8), cInv = col('FACTURA', 9), cDisp = col('DISPLAY', 10);
    for (const r of vm.slice(1)) {
      const d = toDate(r[cDate]);
      if (!d || typeof r[cCust] !== 'string' || !r[cProd]) continue;
      const qty = num(r[cQty]);
      if (!qty || qty <= 0) continue;
      sales.push({
        date: isoDay(d),
        customer: r[cCust].trim(),
        code: String(r[cProd]).trim().toUpperCase(),
        quantity: qty,
        unitPrice: num(r[cPrice]) || 0,
        paid: r[cPaid] === true || norm(r[cPaid]) === 'TRUE' || norm(r[cPaid]) === 'SI',
        paymentMethod: typeof r[cMethod] === 'string' ? r[cMethod].trim() : null,
        invoiced: ['HECHA', 'HECHO', 'SI'].includes(norm(r[cInv])),
        displays: Math.round(num(r[cDisp]) || 0),
      });
    }
  }

  // Precio de lista sugerido: el precio unitario más usado en las ventas de ese código.
  for (const p of products) {
    const counts = new Map<number, number>();
    sales.filter(s => s.code === p.code && s.unitPrice > 0).forEach(s => counts.set(s.unitPrice, (counts.get(s.unitPrice) || 0) + 1));
    const top = Array.from(counts.entries()).sort((a, b) => b[1] - a[1])[0];
    if (top) p.price = String(top[0]);
  }
  // Códigos vendidos que no están en el inventario (p. ej. productos discontinuados).
  for (const code of Array.from(new Set(sales.map(s => s.code)))) {
    if (!products.some(p => p.code === code)) products.push({ code, name: code, stock: null, unitCost: '', price: '' });
  }
  return { products, materials, customers, sales };
}

const ImportSpreadsheetPage: React.FC = () => {
  const [fileName, setFileName] = useState('');
  const [parsed, setParsed] = useState<ReturnType<typeof parseWorkbook> | null>(null);
  const [products, setProducts] = useState<ProductDraft[]>([]);
  const [unpaidDueDays, setUnpaidDueDays] = useState(30);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<Record<string, number> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onFile = async (file: File) => {
    setError(null);
    setResult(null);
    try {
      const wb = XLSX.read(await file.arrayBuffer(), { cellDates: true });
      const p = parseWorkbook(wb);
      if (!p.customers.length && !p.sales.length && !p.products.length) {
        setError('No encontré las hojas INVENTARIO, CLIENTES o VENTA MAYORISTA en este archivo.');
        return;
      }
      setFileName(file.name);
      setParsed(p);
      setProducts(p.products);
    } catch {
      setError('No se pudo leer el archivo. ¿Es un .xlsx?');
    }
  };

  const doImport = async () => {
    if (!parsed) return;
    setImporting(true);
    setError(null);
    try {
      const res = await api.importSpreadsheet({
        products: products.map(p => ({ code: p.code, name: p.name, stock: p.stock, unitCost: Number(p.unitCost) || null, price: Number(p.price) || null })),
        materials: parsed.materials,
        customers: parsed.customers,
        sales: parsed.sales,
        unpaidDueDays,
      });
      setResult(res);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo importar');
    } finally {
      setImporting(false);
    }
  };

  const unpaid = parsed ? parsed.sales.filter(s => !s.paid).reduce((acc, s) => acc + s.quantity * s.unitPrice, 0) : 0;
  const orders = parsed ? new Set(parsed.sales.map(s => `${s.date}|${s.customer.toUpperCase()}`)).size : 0;

  return (
    <Box>
      <Typography variant="h5" fontWeight={800}>Importar planilla</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Carga productos con su stock, insumos de packaging, clientes con su precio pactado y el historial de ventas mayoristas.
        Se puede volver a correr: no duplica clientes, productos ni pedidos. Las ventas históricas no mueven stock: queda el de la hoja INVENTARIO.
      </Typography>
      <Button variant="contained" component="label" startIcon={<UploadFile />}>
        Elegir archivo .xlsx
        <input hidden type="file" accept=".xlsx,.xls" onChange={e => e.target.files?.[0] && onFile(e.target.files[0])} />
      </Button>
      {fileName && <Chip label={fileName} sx={{ ml: 2 }} />}
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

      {parsed && (
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            {[
              ['Productos', products.length],
              ['Insumos', parsed.materials.length],
              ['Clientes', parsed.customers.length],
              ['Pedidos', orders],
              ['Renglones de venta', parsed.sales.length],
              ['A cobrar (sin pagar)', formatMoney(unpaid)],
            ].map(([k, v]) => (
              <Grid item xs={6} md={2} key={k as string}>
                <Card variant="outlined"><CardContent>
                  <Typography variant="body2" color="text.secondary">{k}</Typography>
                  <Typography variant="h6" fontWeight={800}>{v}</Typography>
                </CardContent></Card>
              </Grid>
            ))}
          </Grid>

          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700}>Productos</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Completá el costo por unidad (con él se calcula la ganancia del stock actual). El precio de lista viene del precio más usado en las ventas.
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Código</TableCell>
                    <TableCell>Nombre</TableCell>
                    <TableCell align="right">Stock</TableCell>
                    <TableCell align="right">Costo / u</TableCell>
                    <TableCell align="right">Precio de lista</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {products.map((p, i) => (
                    <TableRow key={p.code}>
                      <TableCell>{p.code}</TableCell>
                      <TableCell>
                        <TextField size="small" value={p.name} onChange={e => setProducts(x => x.map((y, idx) => (idx === i ? { ...y, name: e.target.value } : y)))} />
                      </TableCell>
                      <TableCell align="right">{p.stock ?? '—'}</TableCell>
                      <TableCell align="right">
                        <TextField size="small" type="number" value={p.unitCost} sx={{ width: 110 }} onChange={e => setProducts(x => x.map((y, idx) => (idx === i ? { ...y, unitCost: e.target.value } : y)))} />
                      </TableCell>
                      <TableCell align="right">
                        <TextField size="small" type="number" value={p.price} sx={{ width: 110 }} onChange={e => setProducts(x => x.map((y, idx) => (idx === i ? { ...y, price: e.target.value } : y)))} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <TextField size="small" type="number" label="Plazo de las ventas sin pagar (días)" value={unpaidDueDays}
              onChange={e => setUnpaidDueDays(Number(e.target.value) || 0)} sx={{ width: 260 }} />
            <Button variant="contained" color="success" size="large" startIcon={<CloudUpload />} onClick={doImport} disabled={importing}>
              {importing ? 'Importando…' : 'Importar'}
            </Button>
          </Stack>

          {result && (
            <Alert severity="success">
              Listo: {result.productsCreated} productos nuevos ({result.productsUpdated} actualizados) · {result.materialsCreated} insumos nuevos ·{' '}
              {result.customersCreated} clientes nuevos ({result.customersUpdated} actualizados) · {result.customerPrices} precios pactados ·{' '}
              {result.ordersCreated} pedidos importados{result.ordersSkipped ? ` (${result.ordersSkipped} ya estaban)` : ''}
              {result.salesWithoutProduct ? ` · ${result.salesWithoutProduct} renglones sin producto` : ''}.
            </Alert>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default ImportSpreadsheetPage;
